import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { formatApiError } from '@/lib/api';
import api from '@/lib/api';
import { AlertCircle, Shield, Loader2, CheckCircle, Eye, EyeOff } from 'lucide-react';

const MSG91_WIDGET_SCRIPT = 'https://verify.msg91.com/otp-provider.js';
const MSG91_WIDGET_FALLBACK = 'https://verify.phone91.com/otp-provider.js';

/**
 * Load the MSG91 OTP Widget script once, with a fallback CDN. Resolves with
 * `true` when `window.initSendOTP` is callable; rejects on hard failure.
 */
function loadMsg91Widget() {
  if (typeof window === 'undefined') return Promise.resolve(false);
  if (typeof window.initSendOTP === 'function') return Promise.resolve(true);
  return new Promise((resolve, reject) => {
    const tryLoad = (url, isFallback) => {
      const existing = document.querySelector(`script[src="${url}"]`);
      if (existing) {
        if (typeof window.initSendOTP === 'function') return resolve(true);
        existing.addEventListener('load', () => resolve(typeof window.initSendOTP === 'function'));
        existing.addEventListener('error', () => isFallback ? reject(new Error('msg91_widget_load_failed')) : tryLoad(MSG91_WIDGET_FALLBACK, true));
        return;
      }
      const s = document.createElement('script');
      s.src = url; s.async = true; s.type = 'text/javascript';
      s.onload = () => resolve(typeof window.initSendOTP === 'function');
      s.onerror = () => isFallback ? reject(new Error('msg91_widget_load_failed')) : tryLoad(MSG91_WIDGET_FALLBACK, true);
      document.head.appendChild(s);
    };
    tryLoad(MSG91_WIDGET_SCRIPT, false);
  });
}

export default function LoginPage() {
  const { user, loading: authLoading, setAuthFromResponse, loginWithOtp } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirect = searchParams.get('redirect') || '/';

  useEffect(() => {
    if (!authLoading && user) {
      if (user.role === 'admin' || user.role === 'country_admin') navigate('/admin', { replace: true });
      else if (user.role === 'doctor') navigate('/dashboard', { replace: true });
      else if (user.role === 'patient') navigate('/my-clinics', { replace: true });
      else navigate('/', { replace: true });
    }
  }, [user, authLoading, navigate]);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Patient passwordless login (mobile + OTP) — secondary to email/password.
  const [mobileMode, setMobileMode] = useState(false);
  const [mPhone, setMPhone] = useState('');
  const [mOtpSent, setMOtpSent] = useState(false);
  const [mOtpToken, setMOtpToken] = useState('');
  const [mOtpCode, setMOtpCode] = useState('');
  const [mDevOtp, setMDevOtp] = useState('');
  const [mBusy, setMBusy] = useState(false);
  const [mErr, setMErr] = useState('');

  // 2FA state
  const [show2FA, setShow2FA] = useState(false);
  const [otpToken, setOtpToken] = useState('');
  const [otpCode, setOtpCode] = useState('');
  // Number of digit boxes to render in the OTP input. Backend tells us
  // exactly this on /auth/login → defaults to 4 (matches MSG91 widget
  // config); switches to 6 if MSG91_OTP_LENGTH=6 in backend .env.
  const [otpLength, setOtpLength] = useState(4);
  const [devOtp, setDevOtp] = useState('');
  const [userName, setUserName] = useState('');
  const [deliveryMethod, setDeliveryMethod] = useState('push');
  const [userPhoneE164, setUserPhoneE164] = useState('');
  const [userPhoneMasked, setUserPhoneMasked] = useState('');
  const [pushReceived, setPushReceived] = useState(false);
  const [autoFellBack, setAutoFellBack] = useState(false);
  const [autoSmsFlashing, setAutoSmsFlashing] = useState(false);
  const [widgetLoading, setWidgetLoading] = useState(false);
  const [widgetError, setWidgetError] = useState('');
  const widgetMountedRef = useRef(false);

  // PIN-mode state (hybrid 2FA: trusted device skips OTP, asks PIN instead)
  const [pinToken, setPinToken] = useState('');
  const [pinCode, setPinCode] = useState('');

  // Push notification → auto-fill OTP code (existing behaviour, unchanged).
  useEffect(() => {
    const handler = (event) => {
      if (event.data?.type === 'OTP_RECEIVED' && event.data?.code) {
        setOtpCode(event.data.code);
        setPushReceived(true);
      }
    };
    navigator.serviceWorker?.addEventListener('message', handler);
    return () => navigator.serviceWorker?.removeEventListener('message', handler);
  }, []);

  // Mount the MSG91 OTP Widget. The widget owns send + verify. When the user
  // finishes entering the code in the widget, we receive an access-token in
  // the success callback and POST it to the backend for server-side
  // verifyAccessToken validation. Doctor never types the code on our screen.
  const launchMsg91Widget = useCallback(async (phoneE164) => {
    if (widgetMountedRef.current) return;
    setWidgetLoading(true);
    setWidgetError('');
    try {
      const ok = await loadMsg91Widget();
      if (!ok || typeof window.initSendOTP !== 'function') throw new Error('msg91_widget_load_failed');
      const widgetId = process.env.REACT_APP_MSG91_WIDGET_ID;
      const tokenAuth = process.env.REACT_APP_MSG91_WIDGET_TOKEN || '';
      if (!widgetId) throw new Error('msg91_widget_id_missing');
      widgetMountedRef.current = true;
      window.initSendOTP({
        widgetId,
        tokenAuth,
        identifier: phoneE164 || undefined,
        exposeMethods: false,
        success: async (data) => {
          // data: { message, type, ... } — message holds the access-token JWT.
          const accessToken = data?.message || data?.['access-token'] || data?.access_token || '';
          if (!accessToken) {
            setWidgetError('Verification did not return a token. Please try again.');
            widgetMountedRef.current = false;
            return;
          }
          try {
            const { data: out } = await api.post('/auth/verify-msg91-widget-token', {
              otp_token: otpToken, access_token: accessToken,
            });
            localStorage.setItem('token', out.token);
            setAuthFromResponse(out);
            const role = out.user.role;
            if (role === 'admin' || role === 'country_admin') navigate('/admin');
            else if (role === 'doctor' && redirect === '/') navigate('/dashboard');
            else if (role === 'patient' && redirect === '/') navigate('/my-clinics');
            else navigate(redirect);
          } catch (err) {
            setWidgetError(formatApiError(err.response?.data?.detail) || err.message);
            widgetMountedRef.current = false;
          }
        },
        failure: (err) => {
          setWidgetError(typeof err === 'string' ? err : (err?.message || 'Verification cancelled. Tap below to try again.'));
          widgetMountedRef.current = false;
        },
      });
    } catch (e) {
      setWidgetError('Verification widget could not load. Check your internet connection and tap below to retry.');
      widgetMountedRef.current = false;
    } finally {
      setWidgetLoading(false);
    }
  }, [otpToken, navigate, redirect, setAuthFromResponse]);

  // Switch the in-flight token from push → MSG91 widget. Used by the auto-
  // fallback after 4 seconds of silence AND by the manual button.
  const switchToWidget = useCallback(async ({ silent = false } = {}) => {
    if (!otpToken) return;
    if (!silent) setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/login-otp/resend-sms', { otp_token: otpToken });
      setDeliveryMethod(data.delivery || 'msg91_widget');
      if (data.user_phone_e164) setUserPhoneE164(data.user_phone_e164);
      if (data.user_phone_masked) setUserPhoneMasked(data.user_phone_masked);
      if (typeof data.otp_length === 'number') setOtpLength(data.otp_length);
      setOtpCode('');
      setDevOtp(data._dev_otp || '');
      // Auto-launch widget right away — non-techie doctors should not have
      // to find a button. Skipped if SMS_DEV_MODE returned a dev OTP.
      if ((data.delivery || 'msg91_widget') === 'msg91_widget') {
        launchMsg91Widget(data.user_phone_e164 || userPhoneE164);
      }
    } catch (err) {
      if (!silent) setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [otpToken, userPhoneE164, launchMsg91Widget]);

  // Auto-fallback: at 4 seconds, if push hasn't been received, flash a
  // pulsing message AND silently switch to MSG91 widget so the doctor
  // is never staring at a blank screen.
  useEffect(() => {
    if (!show2FA) return;
    if (deliveryMethod !== 'push') return;
    if (pushReceived || autoFellBack) return;
    const t = setTimeout(() => {
      setAutoSmsFlashing(true);
      setAutoFellBack(true);
      switchToWidget({ silent: true });
    }, 4000);
    return () => clearTimeout(t);
  }, [show2FA, deliveryMethod, pushReceived, autoFellBack, switchToWidget]);

  // Direct widget launch when the backend says delivery=msg91_widget upfront
  // (i.e. user has no push subscription at all on this account).
  useEffect(() => {
    if (!show2FA) return;
    if (deliveryMethod !== 'msg91_widget') return;
    if (widgetMountedRef.current) return;
    launchMsg91Widget(userPhoneE164);
  }, [show2FA, deliveryMethod, userPhoneE164, launchMsg91Widget]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const { data } = await api.post('/auth/login', { email, password });
      if (data.requires_2fa) {
        // Hybrid 2FA: trusted device returns `next: 'pin'` with pin_token
        if (data.next === 'pin' || data.delivery === 'pin') {
          setPinToken(data.pin_token);
          setUserName(data.user_name || '');
          setDeliveryMethod('pin');
          setShow2FA(true);
          setLoading(false);
          return;
        }
        setOtpToken(data.otp_token);
        setDevOtp(data._dev_otp || '');
        setUserName(data.user_name || '');
        setDeliveryMethod(data.delivery || 'msg91_widget');
        setUserPhoneE164(data.user_phone_e164 || '');
        setUserPhoneMasked(data.user_phone_masked || '');
        setOtpLength(typeof data.otp_length === 'number' ? data.otp_length : 4);
        setOtpCode('');
        setShow2FA(true);
        setLoading(false);
        return;
      }
      // Direct login (patients, admins)
      localStorage.setItem('token', data.token);
      setAuthFromResponse(data);
      const role = data.user.role;
      if (role === 'admin' || role === 'country_admin') navigate('/admin');
      else if (role === 'doctor' && redirect === '/') navigate('/dashboard');
      else if (role === 'patient' && redirect === '/') navigate('/my-clinics');
      else navigate(redirect);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPin = async () => {
    if (!/^\d{6}$/.test(pinCode)) { setError('Enter your 6-digit PIN'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/pin/verify', { pin_token: pinToken, pin: pinCode });
      localStorage.setItem('token', data.token);
      setAuthFromResponse(data);
      const role = data.user.role;
      if (role === 'admin' || role === 'country_admin') navigate('/admin');
      else if (role === 'doctor' && redirect === '/') navigate('/dashboard');
      else if (role === 'patient' && redirect === '/') navigate('/my-clinics');
      else navigate(redirect);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    if (!otpCode.trim()) { setError('Please enter the OTP'); return; }
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/auth/verify-login-otp', { otp_token: otpToken, code: otpCode.trim() });
      localStorage.setItem('token', data.token);
      setAuthFromResponse(data);
      if (data.user.role === 'doctor' && redirect === '/') navigate('/dashboard');
      else if (data.user.role === 'patient' && redirect === '/') navigate('/my-clinics');
      else navigate(redirect);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const mSendOtp = async () => {
    const d = mPhone.replace(/\D/g, '');
    if (d.length < 10) { setMErr('Enter a valid 10-digit mobile number'); return; }
    setMBusy(true); setMErr(''); setMDevOtp('');
    try {
      const { data } = await api.post('/auth/send-otp', { phone: d });
      setMOtpToken(data.otp_token);
      setMOtpSent(true);
      if (data._dev_otp) setMDevOtp(String(data._dev_otp));
    } catch (e) {
      setMErr(formatApiError(e.response?.data?.detail) || 'Could not send OTP. Try again.');
    } finally { setMBusy(false); }
  };

  const mVerify = async () => {
    if (!mOtpCode || mOtpCode.replace(/\D/g, '').length < 4) { setMErr('Enter the code sent to your phone'); return; }
    setMBusy(true); setMErr('');
    try {
      await api.post('/auth/verify-otp', { otp_token: mOtpToken, code: mOtpCode.replace(/\D/g, '') });
      const data = await loginWithOtp({ phone: mPhone.replace(/\D/g, ''), otp_token: mOtpToken });
      const role = data.user.role;
      if (role === 'patient' && redirect === '/') navigate('/my-clinics');
      else navigate(redirect);
    } catch (e) {
      setMErr(formatApiError(e.response?.data?.detail) || 'Verification failed. Try again.');
    } finally { setMBusy(false); }
  };


  const isWidgetMode = deliveryMethod === 'msg91_widget';
  const isPushMode = deliveryMethod === 'push';
  const isPinMode = deliveryMethod === 'pin';
  const isSmsDevMode = deliveryMethod === 'sms';

  return (
    <div className="min-h-screen bg-slate-50/50 flex items-center justify-center px-6 py-12" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src="/docsite-logo.png" alt="DocSite" className="w-24 h-24 object-contain mx-auto mb-4" />
          <h1 className="text-2xl font-medium text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {show2FA ? 'Two-Factor Authentication' : 'Welcome Back'}
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            {show2FA ? `Hi ${userName}, verify your identity to continue` : 'Sign in to your account'}
          </p>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 md:p-8">
          {show2FA ? (
            <div className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 text-sm text-red-600" data-testid="login-error">
                  <AlertCircle size={14} /> {error}
                </div>
              )}

              {/* PIN-mode (trusted device) */}
              {isPinMode && (
                <div className="space-y-4" data-testid="login-pin-block">
                  <div className="bg-violet-50 rounded-xl p-4 text-center">
                    <Shield size={24} className="text-violet-600 mx-auto mb-2" />
                    <p className="text-sm text-violet-800 font-medium">Enter your DocSite PIN</p>
                    <p className="text-xs text-violet-600 mt-1">
                      We recognise this device. Type the 6-digit PIN you set on your dashboard to sign in instantly — no SMS, no waiting.
                    </p>
                  </div>
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1.5 block">Your 6-digit PIN</label>
                    <input
                      type="password" inputMode="numeric" maxLength={6} autoFocus
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-base text-center font-mono tracking-[0.5em] outline-none focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
                      value={pinCode} onChange={(e) => setPinCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      placeholder="••••••" data-testid="login-pin-input"
                      onKeyDown={(e) => { if (e.key === 'Enter') handleVerifyPin(); }}
                    />
                  </div>
                  <button onClick={handleVerifyPin} disabled={loading || pinCode.length !== 6}
                    className="w-full rounded-full py-3 bg-violet-600 text-white font-medium hover:bg-violet-700 transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="verify-pin-button">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                    {loading ? 'Verifying…' : 'Sign In'}
                  </button>
                  <Link to="/forgot-pin" className="block text-center text-xs text-violet-700 hover:text-violet-900 underline underline-offset-2"
                    data-testid="login-forgot-pin">
                    Forgot PIN? Reset via email
                  </Link>
                </div>
              )}

              {/* Push-mode banner */}
              {isPushMode && (
                <div className="bg-sky-50 rounded-xl p-4 text-center" data-testid="login-push-banner">
                  <Shield size={24} className="text-sky-600 mx-auto mb-2" />
                  <p className="text-sm text-sky-800 font-medium">Login code sent via notification</p>
                  <p className="text-xs text-sky-600 mt-1">
                    A login code has been sent to the DocSite app on your phone. Open the notification and tap it, or read the 4-digit code and type it below.
                  </p>
                  {autoSmsFlashing && (
                    <p className="text-xs text-amber-700 mt-2 font-semibold animate-pulse" data-testid="login-autosms-flash">
                      Notification didn't arrive — switching to SMS verification, please wait…
                    </p>
                  )}
                </div>
              )}

              {/* Widget-mode banner */}
              {isWidgetMode && (
                <div className="bg-emerald-50 rounded-xl p-4 text-center" data-testid="login-widget-banner">
                  <Shield size={24} className="text-emerald-600 mx-auto mb-2" />
                  <p className="text-sm text-emerald-800 font-medium">Verifying via SMS</p>
                  <p className="text-xs text-emerald-700 mt-1">
                    {userPhoneMasked ? <>Sending a verification code to <span className="font-mono">{userPhoneMasked}</span>. </> : null}
                    A verification window will open in a moment — enter the code there to sign in.
                  </p>
                  {widgetLoading && (
                    <p className="text-xs text-emerald-600 mt-2 flex items-center justify-center gap-1.5" data-testid="login-widget-loading">
                      <Loader2 size={12} className="animate-spin" /> Loading verification…
                    </p>
                  )}
                  {widgetError && (
                    <p className="text-xs text-red-600 mt-2" data-testid="login-widget-error">
                      {widgetError}
                    </p>
                  )}
                </div>
              )}

              {/* OTP entry — only used in push mode (or SMS_DEV_MODE).
                  Widget mode handles entry inside MSG91's widget UI. */}
              {(isPushMode || isSmsDevMode) && (
                <>
                  {devOtp && (
                    <div className="bg-amber-50 rounded-lg p-3 text-xs text-amber-700 text-center" data-testid="dev-otp-hint">
                      Dev mode — OTP: <strong className="font-mono">{devOtp}</strong>
                    </div>
                  )}
                  <div>
                    <label className="text-xs font-medium text-slate-600 mb-1.5 block">Enter OTP</label>
                    <input type="text" maxLength={otpLength}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm text-center font-mono tracking-[0.5em] outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                      value={otpCode} onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, otpLength))}
                      placeholder={'-'.repeat(otpLength)} autoFocus data-testid="login-2fa-input" />
                  </div>
                  <button onClick={handleVerify2FA} disabled={loading}
                    className="w-full rounded-full py-3 bg-sky-600 text-white font-medium hover:bg-sky-700 transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                    data-testid="verify-2fa-button">
                    {loading ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                    {loading ? 'Verifying...' : 'Verify & Sign In'}
                  </button>
                  {isPushMode && !pushReceived && (
                    <button
                      onClick={() => switchToWidget({ silent: false })}
                      disabled={loading}
                      className="w-full text-xs text-sky-700 hover:text-sky-900 underline underline-offset-2 disabled:opacity-50"
                      data-testid="login-resend-sms-button"
                    >
                      {loading ? 'Switching to SMS…' : "Didn't get the notification? Verify via SMS instead"}
                    </button>
                  )}
                </>
              )}

              {/* Manual retry for widget if it errored */}
              {isWidgetMode && widgetError && (
                <button onClick={() => { widgetMountedRef.current = false; launchMsg91Widget(userPhoneE164); }}
                  className="w-full rounded-full py-3 bg-emerald-600 text-white font-medium hover:bg-emerald-700 transition-colors text-sm flex items-center justify-center gap-2"
                  data-testid="login-widget-retry">
                  Retry SMS verification
                </button>
              )}

              <button onClick={() => { setShow2FA(false); setOtpCode(''); setError(''); setDevOtp(''); widgetMountedRef.current = false; setWidgetError(''); setAutoFellBack(false); setAutoSmsFlashing(false); }}
                className="w-full text-xs text-slate-500 hover:text-slate-700 py-2" data-testid="back-to-login-form">
                Back to login
              </button>
            </div>
          ) : (
            <>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 rounded-xl bg-red-50 text-sm text-red-600" data-testid="login-error">
                    <AlertCircle size={14} /> {error}
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-slate-600 mb-1.5 block">Email</label>
                  <input type="email" required className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                    value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="login-email-input" />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <label className="text-xs font-medium text-slate-600">Password</label>
                    <Link to="/forgot-password" className="text-xs text-sky-600 hover:text-sky-700 font-medium" data-testid="forgot-password-link">Forgot Password?</Link>
                  </div>
                  <div className="relative">
                    <input type={showPassword ? 'text' : 'password'} required className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 pr-10 text-sm focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 outline-none"
                      value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" data-testid="login-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                      data-testid="toggle-password-visibility" tabIndex={-1}>
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <button type="submit" disabled={loading} className="w-full rounded-full py-3 bg-sky-600 text-white font-medium hover:bg-sky-700 transition-colors text-sm disabled:opacity-50 flex items-center justify-center gap-2" data-testid="login-submit-button">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : null}
                  {loading ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
              <div className="relative my-5">
                <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200" /></div>
                <div className="relative flex justify-center"><span className="bg-white px-3 text-xs text-slate-400">or</span></div>
              </div>
              {!mobileMode ? (
                <button onClick={() => { setMobileMode(true); setMErr(''); }}
                  className="w-full rounded-full py-3 border border-sky-200 bg-sky-50 text-sky-700 font-semibold text-sm hover:bg-sky-100 transition-colors"
                  data-testid="login-mobile-otp-toggle">
                  Patient? Continue with mobile + OTP
                </button>
              ) : (
                <div className="space-y-2.5" data-testid="login-mobile-otp-block">
                  <p className="text-xs text-slate-500">Patients can sign in with just their mobile number — no password needed.</p>
                  {mErr && <div className="flex items-center gap-2 p-2.5 rounded-xl bg-red-50 text-xs text-red-600" data-testid="login-mobile-error"><AlertCircle size={13} />{mErr}</div>}
                  {!mOtpSent ? (
                    <>
                      <input type="tel" inputMode="numeric" value={mPhone}
                        onChange={(e) => setMPhone(e.target.value.replace(/[^0-9]/g, '').slice(0, 13))}
                        placeholder="Mobile number"
                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                        data-testid="login-mobile-phone" />
                      <button onClick={mSendOtp} disabled={mBusy}
                        className="w-full rounded-full py-3 bg-sky-600 text-white font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="login-mobile-send-otp">
                        {mBusy ? <Loader2 size={16} className="animate-spin" /> : null}{mBusy ? 'Sending…' : 'Send OTP'}
                      </button>
                    </>
                  ) : (
                    <>
                      {mDevOtp && <p className="text-[11px] text-amber-700 text-center">Dev OTP: <b>{mDevOtp}</b></p>}
                      <input type="text" inputMode="numeric" value={mOtpCode}
                        onChange={(e) => setMOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter OTP" autoFocus
                        className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm text-center tracking-[0.4em] outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500"
                        data-testid="login-mobile-otp-code" />
                      <button onClick={mVerify} disabled={mBusy}
                        className="w-full rounded-full py-3 bg-sky-600 text-white font-medium text-sm disabled:opacity-50 flex items-center justify-center gap-2"
                        data-testid="login-mobile-verify">
                        {mBusy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}{mBusy ? 'Verifying…' : 'Verify & Sign In'}
                      </button>
                      <button onClick={() => { setMOtpSent(false); setMOtpCode(''); setMErr(''); }} className="w-full text-xs text-slate-500 hover:text-slate-700" data-testid="login-mobile-change">Change number</button>
                    </>
                  )}
                  <button onClick={() => { setMobileMode(false); setMOtpSent(false); setMOtpCode(''); setMErr(''); }} className="w-full text-xs text-sky-600 font-medium" data-testid="login-use-email">Use email &amp; password instead</button>
                </div>
              )}
              <p className="text-center text-sm text-slate-500 mt-6">
                Don't have an account? <Link to="/register" className="text-sky-600 font-medium hover:text-sky-700" data-testid="register-link">Create one</Link>
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
